<template>
    <v-dialog v-model="dialog" max-width="350" transition="dialog-bottom-transition" @keydown.enter="save" @keydown.esc="cancel">
        <v-card>
            <v-toolbar dark color="primary">
                <v-btn icon dark :disabled="loading || editing" @click="cancel">
                    <v-icon large>close</v-icon>
                </v-btn>
                <v-toolbar-title>Packaging</v-toolbar-title>
            </v-toolbar>
            <v-layout column v-if="page == 1">
                <v-list>
                    <v-list-tile v-for="(item, i) in plantUnits" :key="i">
                        <v-list-tile-content>
                            <v-checkbox :label="item.UnitName" :disabled="readOnly(item.PlantUnitID)" v-model="selectedPlantUnits[item.PlantUnitID]"></v-checkbox>                            
                        </v-list-tile-content>               
                    </v-list-tile>
                </v-list>
            </v-layout>
            <v-layout column v-if="page == 2">
                <v-data-table
                        :headers="headers"
                        :items="volumes"
                        class="elevation-1"
                        hide-actions
                    >
                    <template v-slot:items="props">
                        <td class="text-xs-center">{{ props.item.UnitName }}</td>                                                
                        <td class="text-xs-center" @click="showEditVolume(props.index)" tabindex="0" @focus="showEditVolume(props.index)">
                            <div v-if="!editVolume[props.index]" class="row">
                                {{ `${props.item.Volume || 0}`}}
                            </div>
                            <div class="row" v-else>      
                                <EditTableCell
                                    :rules="[rules.requiredNumber, rules.integerOnly, rules.fromAToB(0, 99999)]"
                                    :value="props.item.Volume == null ? '' : props.item.Volume.toString()"
                                    @input="handleVolume(props.item, props.index, $event)"
                                    @close="hideEditVolume(props.index)" 
                                    :loading="loading">
                                </EditTableCell>
                            </div>
                        </td>
                    </template>
                    <template v-slot:footer>
                        <tr>
                            <td>Total Volume</td>
                            <td class="text-xs-center" @click="editTotal = true" tabindex="0" @focus="editTotal = true">
                                <div v-if="!editTotal" class="row">
                                    {{ `${totalVolume}`}}
                                </div>
                                <div class="row" v-else> 
                                    <EditTableCell
                                        :rules="[rules.requiredNumber, rules.integerOnly, rules.fromAToB(0, 99999)]"
                                        :value="totalVolume == null ? '' : totalVolume.toString()"
                                        @input="modifyTotalVolume"
                                        @close="modifyTotalVolume"
                                        suffix="kg">
                                    </EditTableCell>
                                </div>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-layout>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="save" :disabled="loading || editing || totalSelectedPlantUnits == 0 || totalSelectedNonReadOnlyPlantUnits == 0" :loading="loading" color="primary">{{ totalSelectedPlantUnits > 1 && page == 1 ? 'Next' : 'Save' }}</v-btn>
                <v-btn @click="cancel" :disabled="loading || editing" color="error">Cancel</v-btn>
            </v-card-actions>
        </v-card>
        <ConfirmationDialog 
            DialogTitle="Volume Change"
            DialogContents="Should the volumes for the other packaging types be adjusted or not?"
            :DisplayDialog="showConfirmation"
            @ClickedYes="clickedYes"
            @ClickedNo="clickedNo"
            @ClickedCancel="clickedCancel">
        </ConfirmationDialog>
    </v-dialog>
</template>
<script>
import EditTableCell from '@/components/berriesRoster/EditTableCell.vue'
import ConfirmationDialog from '@/components/harvestPlan/HarvestPlanPackagingDialogConfirmation.vue'
import validationRules from "@/helpers/validationrules";

export default {
    components: {
        EditTableCell,
        ConfirmationDialog
    },
    props: {
        plantUnits: {
            required: true,
            type: Array
        },
        packages: {
            required: true,
            type: Array
        },
        uom: {
            required: true,
            type: String
        }
    },
    data() {
        return {
            showConfirmation: false,
            editIndex: -1,
            editAmount: 0,
            editDetail: null,
            dialog: true,
            loading: false,
            selectedPlantUnits: {},
            editVolume:[],
            editTotal:false,
            volumes: [],
            totalVolume: 0,
            defaultVolumes: true,
            page:1,
            rules: validationRules.rules,
            headers: [
                {
                    text: 'Packaging',
                    value: 'UnitName',
                    class:"text-xs-center"
                },               
                {   text: `Volume ${this.uom == this.$Constants.HARVESTPLAN_UOM_ENUM.KG.ID ? '(Kgs)' : '(Trays)'}`,
                    value: 'Volume', 
                    width: 150,
                    class:"text-xs-center" }
            ]
        }
    },
    watch: {
        dialog(val) {
            if (!val) {
                this.cancel();
            }
        }
    },
    computed: {
        totalSelectedPlantUnits() {
            let total = 0;            
            this.plantUnits.forEach(pu => {
                if (this.selectedPlantUnits[pu.PlantUnitID]) {
                    total += 1;
                }
            })

            return total;
        },
        totalSelectedNonReadOnlyPlantUnits() {
            let total = 0;            
            this.plantUnits.forEach(pu => {
                if (this.selectedPlantUnits[pu.PlantUnitID]) {
                    let match = this.packages.find(p => p.PlantUnitID == pu.PlantUnitID);

                    if (!match || !match.ReadOnly) {
                        total += 1;
                    }
                }
            })

            return total;
        },
        editing() {
            return this.editTotal || this.editVolume.some(v => v == true);
        }      
    },
    methods: {
        clickedYes() {
            this.showConfirmation = false;

            // lets get the difference between the original amount and the edited amount
            let difference = this.editDetail.Volume - parseInt(this.editAmount);

            // if we are using trays then lets first convert the difference to KG
            if(this.uom == this.$Constants.HARVESTPLAN_UOM_ENUM.TRAYSPUNNETS.ID) {
                difference = difference * this.editDetail.AveKgsPerUnit * (this.editDetail.NumInTray || 1);
            }

            let total = 0;
            // lets get the total volume for other units
            this.volumes.forEach(v => {
                if(v.PlantUnitID != this.editDetail.PlantUnitID) {
                    total += v.Volume;
                }
            });                  

            // lets go and add or subtract the volume from the other selected package rows
            this.volumes.forEach(v => {
                if(v.PlantUnitID != this.editDetail.PlantUnitID) {
                    if(this.uom == this.$Constants.HARVESTPLAN_UOM_ENUM.TRAYSPUNNETS.ID) {                         
                        v.Volume += Math.round((difference * (v.Volume / total)) / v.AveKgsPerUnit / (v.NumInTray || 1));
                    } else {
                        v.Volume += difference * (v.Volume / total);
                    }

                    if (v.Volume < 0) {
                        v.Volume = 0;
                    }
                }
            });

            this.editDetail.Volume = parseInt(this.editAmount);

            this.modifyVolume();
            this.hideEditVolume(this.editIndex);
        },
        clickedNo() {
            this.showConfirmation = false;
            this.editDetail.Volume = parseInt(this.editAmount);
            this.modifyVolume();        
            this.hideEditVolume(this.editIndex);
        },
        clickedCancel() {
            this.showConfirmation = false;
            this.hideEditVolume(this.editIndex);
        },
        modifyVolume() {
            this.totalVolume = this.volumes.map(v => { return parseInt(v.Volume || 0) }).reduce((a, b) => a + b);
        },
        modifyTotalVolume(event) {
            var amount = event.value;
            if (amount) {
                let oldAmount = this.totalVolume;
                this.totalVolume = parseInt(amount);
                this.volumes.forEach(v => {                
                    let percentage = parseFloat(v.Volume) / (oldAmount == 0 ? parseFloat(v.Volume == 0 ? '1' : v.Volume) : oldAmount);
                    v.Volume = Math.round(this.totalVolume * percentage);                    
                });

                let newTotalVolume = this.volumes.map(v => { return parseInt(v.Volume || 0) }).reduce((a, b) => a + b);

                if (newTotalVolume !== this.totalVolume) {
                    let diff = newTotalVolume - this.totalVolume;
                    let sign = diff > 0 ? -1 : 1;                    
                    while(diff !== 0) {
                        this.volumes.forEach(v => {                            
                            v.Volume = (v.Volume || 0) +  (1 * sign);
                            diff += (1 * sign);                            

                            if (diff === 0) {
                                sign = 0;
                            }                            
                        });
                    }
                }
            }
            
            this.editTotal = false;
        },
        showEditVolume(index) {
            this.$set(this.editVolume, index, true);
        },
        hideEditVolume(index) {            
            this.$set(this.editVolume, index, false);
        },
        async handleVolume(detail, index, event) {
            this.editAmount = event.value;
            this.editIndex = index;
            this.editDetail = detail;
            this.showConfirmation = true;
        },
        readOnly(plantUnitID) {
            let existingPackage = this.packages.find(p => p.PlantUnitID == plantUnitID);

            if (existingPackage) {
                return existingPackage.ReadOnly;
            }

            return false;
        },
        integer(v){            
            if (!v || v === "") {
                return true;
            }

            if(!v.toString().match(/^[0-9]{1,15}$/)){
                return "Please enter a whole positive number";
            }

            return false;
        },
        cancel() {
            this.$emit("cancel");
        },
        save() {
            if (this.loading || this.editing || this.totalSelectedPlantUnits == 0 || this.totalSelectedNonReadOnlyPlantUnits == 0) {
                return;
            }

            this.loading = true;

            if (this.page == 1) {
                this.volumes = [];
                this.plantUnits.forEach(pu => {
                    if(this.selectedPlantUnits[pu.PlantUnitID]) {
                        let match = this.packages.find(p => p.PlantUnitID == pu.PlantUnitID);

                        this.volumes.push({
                            PlantUnitID: pu.PlantUnitID,
                            AveKgsPerUnit: pu.AveKgsPerUnit,
                            NumInTray: pu.NumInTray,
                            UnitName: pu.UnitName,
                            Volume: match ? match.VolumeUOM : null,
                            ReadOnly: match ? match.ReadOnly : false
                        });
                    }
                });

                // If we have de-selected an existing package unit, then assign it to any newly selected package
                this.packages.forEach(p => {
                    if (!p.ReadOnly && !this.selectedPlantUnits[p.PlantUnitID]) {
                        let volume = this.volumes.find(v => !v.ReadOnly && v.Volume == null);

                        if (volume) {
                            volume.Volume = p.VolumeUOM;
                        }
                    }
                })

                if (this.defaultVolumes || this.volumes.length == 1 || this.volumes.every(v => v.Volume == null)) {
                    let totalVolumeKg = this.packages[0].VolumeKg;                    

                    this.volumes.forEach(v => {
                        let vol = this.uom == this.$Constants.HARVESTPLAN_UOM_ENUM.KG.ID ? totalVolumeKg / this.volumes.length : totalVolumeKg / this.volumes.length / v.AveKgsPerUnit / v.NumInTray;
                        vol = Math.round(vol);
                        v.Volume = vol;                        
                    });

                    this.modifyTotalVolume({ value: this.totalVolume});
                }
            }

            if (this.page == 1 && this.totalSelectedPlantUnits > 1 && this.volumes.some(v => !v.ReadOnly)) {
                this.page = 2;
                this.loading = false;
                return;
            }

            if (this.volumes.some(v => !v.ReadOnly))
            {
                this.$emit("save", this.volumes);
            }
            else 
            {
                this.loading = false;
            }
        }
    },
    created() {        
        this.plantUnits.forEach(pu => {
            this.$set(this.selectedPlantUnits, pu.PlantUnitID, false);
        });

        if (this.packages) {
            this.totalVolume = 0;
            this.packages.forEach(p => {                
                if (p.PlantUnitID) {
                    this.defaultVolumes = false;
                    this.selectedPlantUnits[p.PlantUnitID] = true;
                }
                
                this.totalVolume += parseInt(p.VolumeUOM || 0);
            });
        }
    }
}
</script>